import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contactsApi } from '../lib/api';
import type { SavedItem, SavedItemKind } from './useSavedItems';
import type { Followup } from './useFollowups';

// Origine del contatto (come e' entrato nel CRM)
export type ContactSource = 'linkedin' | 'email' | 'manual';

// Contatto come restituito dall'API
export interface Contact {
  id: string;
  full_name: string;
  headline: string | null;
  company: string | null;
  emails: string[];
  linkedin_url: string | null;
  avatar_url: string | null;
  notes: string | null;
  source: ContactSource | string;
  ai_summary: string | null;
  created_at: string;
  updated_at: string;
}

// Dettaglio contatto: GET /contacts/:id ritorna anche gli elementi collegati
export interface ContactDetail extends Contact {
  saved_items: SavedItem[];
  followups: Followup[];
}

// Voce della timeline unificata (mail ricevute + elementi LinkedIn), desc per data.
// I campi extra dipendono dal tipo: teniamo il tipo aperto ma con i campi noti.
export interface TimelineEntry {
  type: 'message' | 'saved_item';
  date: string;
  id?: string;
  // Campi presenti quando type === 'message'
  subject?: string | null;
  from_name?: string | null;
  from_email?: string | null;
  snippet?: string | null;
  ai_summary?: string | null;
  // Campi presenti quando type === 'saved_item'
  kind?: SavedItemKind;
  title?: string | null;
  content?: string | null;
  url?: string | null;
}

export function useContacts() {
  return useQuery({
    queryKey: ['contacts'],
    queryFn: async () => {
      const res = await contactsApi.list();
      const items = (res?.items ?? res?.contacts ?? res ?? []) as Contact[];
      return items.map((c) => ({ ...c, emails: c.emails ?? [] }));
    },
  });
}

export function useContact(id: string | null) {
  return useQuery({
    queryKey: ['contact', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await contactsApi.get(id!);
      const c = (res?.contact ?? res) as ContactDetail;
      return {
        ...c,
        emails: c.emails ?? [],
        saved_items: c.saved_items ?? [],
        followups: c.followups ?? [],
      };
    },
  });
}

export function useContactTimeline(id: string | null) {
  return useQuery({
    queryKey: ['contact-timeline', id],
    enabled: !!id,
    queryFn: async () => {
      const res = await contactsApi.timeline(id!);
      return (res?.items ?? res?.timeline ?? res ?? []) as TimelineEntry[];
    },
  });
}

export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Contact>) => contactsApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contacts'] }),
  });
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Contact> }) =>
      contactsApi.patch(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['contact', vars.id] });
    },
  });
}

export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => contactsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['saved-items'] });
      qc.invalidateQueries({ queryKey: ['followups'] });
    },
  });
}
